/* eslint-disable no-else-return */
/* eslint-disable no-unsafe-optional-chaining */

const ROW_HEIGHT = 34;
const NAME_COLUMN_MIN_WIDTH = 150;
const PROPERTY_COLUMN_MIN_WIDTH = 130;
const SELECT_COLUMN_WIDTH = 40;
/**
 * The "add column" button floats over the grid's top-right corner, on top of
 * whatever header cell ends there. Widening that column alone does not help —
 * its filter icon is drawn at the cell's right edge, so it just moves right
 * with it. The last column therefore gets this much extra width *and* the same
 * amount of padding-right (see DimensionsTableTheme.scss), which leaves the
 * label exactly the room it had before while pushing the icon clear.
 */
const ADD_COLUMN_BUTTON_WIDTH = 34;

const calculatePropertyColumnWidth = (name: string) => {
  const baseWidth = PROPERTY_COLUMN_MIN_WIDTH;
  const multiplier = 8;
  return Math.max(baseWidth, (name?.length || 0) * multiplier + 32);
};

const tableHeaderRow = (
  tableItemsProperty_: any,
  tableProperty_: any,
  selectedDimension: any,
  highlightedColumnId?: string | number | null,
) => {
  const nameOptions = tableItemsProperty_
    .filter((item: any) => item?.name !== null)
    .reduce((accumulator: any, item: any) => {
      if (!accumulator.includes(item?.name)) {
        accumulator.push(item?.name);
      }
      return accumulator;
    }, []);

  const cells = [
    {
      type: 'filter',
      options: nameOptions,
      id: 'name',
      text: 'Name',
      hasDelete: false,
      hasEdit: false,
      nonEditable: true,
      className: 'header-cell header-first',
      isModernFilter: true,
    },
    ...(tableProperty_?.length
      ? tableProperty_?.map((data: any) => {
          const options = tableItemsProperty_
            .filter((item: any) => item[data?.id] !== null)
            .reduce((accumulator: any, item: any) => {
              if (!accumulator.includes(item[data?.id])) {
                accumulator.push(item[data?.id]);
              }
              return accumulator;
            }, []);
          const isColumnHighlighted =
            highlightedColumnId !== null &&
            highlightedColumnId !== undefined &&
            data?.id === highlightedColumnId;
          const row = {
            type: 'filter',
            options,
            id: data?.id,
            hasDelete: true,
            hasEdit: true,
            colType: 'header',
            text: data?.name,
            className: isColumnHighlighted ? 'header-cell column-highlight-new' : 'header-cell ',
            isModernFilter: true,
          };
          return row;
        })
      : []),
  ];

  if (selectedDimension?.name !== 'Time') {
    cells.unshift({
      type: 'select',
      text: '',
      itemId: 0,
      placeholder: 'Enter value',
      property: 'select',
      className: 'select header-select',
      hasFormulaError: false,
      isHeading: true,
    });
  }

  return {
    rowId: 'header',
    // Same as the data rows. A taller header row made the grid look like two
    // different tables stacked; ReactGrid takes the height per row, so this is
    // the only place it needs to agree.
    height: ROW_HEIGHT,
    cells,
  };
};

const getTableRows = (
  tableItemsProperty_: any,
  tableProperty_: any,
  selectedDimension: any,
  highlightedRowIndex?: number | null,
  highlightedColumnId?: string | number | null,
  selectedItemIds?: Set<string | number>,
) => {
  return [
    tableHeaderRow(tableItemsProperty_, tableProperty_, selectedDimension, highlightedColumnId),
    ...(tableItemsProperty_?.length
      ? tableItemsProperty_?.map((item: any, index: any) => {
          // Only the "Enter value" name cell glows — that's the one the
          // cursor actually lands in for a new row, not the whole row.
          const isNameCellHighlighted =
            highlightedRowIndex !== null && index === highlightedRowIndex;
          // The bulk-select checkbox had no visual echo elsewhere in the row —
          // "N Selected" in the action bar was the only proof a selection
          // existed. This tints the whole row so it's obvious which ones are
          // checked without having to look back at the checkbox column.
          const isRowSelected = Boolean(item?.id) && selectedItemIds?.has(item.id);
          const cells = [
            {
              type: 'text',
              placeholder: 'Enter value',
              text: `${item?.name}` ?? '',
              property: 'name',
              rows: tableItemsProperty_?.length,
              className: isNameCellHighlighted ? 'name row-highlight-new' : 'name',
              hasFormulaError: false,
              hideTooltip: true,
              index,
            },
            ...(tableProperty_?.length
              ? tableProperty_?.map((data: any) => {
                  const isColumnHighlighted =
                    highlightedColumnId !== null &&
                    highlightedColumnId !== undefined &&
                    data?.id === highlightedColumnId;
                  const columnHighlightClass = isColumnHighlighted ? 'column-highlight-new' : '';
                  if (
                    data?.name.trim().toLowerCase() === 'start' ||
                    data?.name.trim().toLowerCase() === 'end' ||
                    data?.name.trim().toLowerCase() === 'start date' ||
                    data?.name.trim().toLowerCase() === 'end date'
                  ) {
                    const row = {
                      type: 'date',
                      text: item[`${data?.id}`] || '',
                      date: new Date(),
                      nonEditable: !item?.id,
                      isModernDate: true,
                      className: columnHighlightClass,
                    };
                    return row;
                  } else if (data?.items) {
                    const row = {
                      type: 'dropdown',
                      nonEditable: !item?.id,
                      selectOption: true,
                      selectedValue: item[`${data?.id}`],
                      values: [
                        ...(data?.items?.length
                          ? data?.items?.map((value: any) => ({
                              label: value?.name,
                              value: value?.name,
                            }))
                          : []),
                      ],
                      index,
                      operation: '',
                      data,
                      isModernDropdown: true,
                      className: columnHighlightClass,
                    };
                    return row;
                  } else {
                    const row = {
                      type: 'text',
                      rows: tableItemsProperty_?.length,
                      text: item[`${data?.id}`] ?? '',
                      index,
                      data,
                      nonEditable: !item?.id,
                      isHeading: false,
                      hasFormulaError: false,
                      hideTooltip: true,
                      className: columnHighlightClass,
                    };
                    return row;
                  }
                })
              : []),
          ];
          if (selectedDimension?.name !== 'Time') {
            cells.unshift({
              type: 'select',
              text: '',
              placeholder: 'Enter value',
              itemId: item?.id,
              property: 'select',
              className: 'select',
              hasFormulaError: false,
              isHeading: false,
              index,
            });
          }
          // Off-white tint across the whole row so it reads as distinct from
          // the rest of the table while it's new/unsaved — separate from the
          // name cell's own outline blink above, and on every cell (not just
          // name) since ReactGrid has no single element spanning a row.
          const extraRowClass = isNameCellHighlighted
            ? 'row-tint-new'
            : isRowSelected
            ? 'row-selected-tint'
            : null;
          const rowCells = extraRowClass
            ? cells.map((cell: any) => ({
                ...cell,
                className: cell.className ? `${cell.className} ${extraRowClass}` : extraRowClass,
              }))
            : cells;
          return {
            rowId: index,
            height: ROW_HEIGHT,
            itemId: item?.id,
            itemName: item?.name,
            reorderable: true,
            cells: rowCells,
          };
        })
      : []),
  ];
};

const getTableColumns = (tableProperty_: any, tableReference: any, selectedDimension: any) => {
  const hasSelect = selectedDimension?.name !== 'Time';

  const propertyColumns = (tableProperty_?.length ? tableProperty_ : []).map((data: any) => ({
    columnId: `${data?.id}`,
    name: data?.name,
    width: calculatePropertyColumnWidth(data?.name),
    resizable: true,
    reorderable: true,
  }));

  const fixedWidth =
    (hasSelect ? SELECT_COLUMN_WIDTH : 0) +
    NAME_COLUMN_MIN_WIDTH +
    propertyColumns.reduce((sum: number, col: any) => sum + col.width, 0);

  // Reserve the button's gutter before distributing slack, so handing it to
  // the last column below does not push the grid into horizontal overflow.
  const available = Math.max(0, tableReference - ADD_COLUMN_BUTTON_WIDTH);
  const extraSpace = available > fixedWidth ? available - fixedWidth : 0;

  const columns = [
    {
      columnId: 'name',
      width: NAME_COLUMN_MIN_WIDTH + extraSpace,
      resizable: true,
    },
    ...propertyColumns,
  ];

  // Give the gutter to whichever column actually ends up last.
  const lastColumn = columns[columns.length - 1];
  if (lastColumn) lastColumn.width += ADD_COLUMN_BUTTON_WIDTH;

  if (hasSelect) {
    columns.unshift({
      columnId: 'select',
      width: SELECT_COLUMN_WIDTH,
      resizable: true,
    });
  }

  return columns;
};

const reorderArray = <T extends {}>(array: T[], idxs: number[], to: number) => {
  const movedElements = array.filter((_, index) => idxs.includes(index));
  const targetIndex =
    Math.min(...idxs) < to ? (to += 1) : (to -= idxs.filter((index) => index < to).length);
  const leftSide = array.filter((_, index) => index < targetIndex && !idxs.includes(index));
  const rightSide = array.filter((_, index) => index >= targetIndex && !idxs.includes(index));
  const reorderedArray = [...leftSide, ...movedElements, ...rightSide];
  const idsOnlyArray = reorderedArray.map((item: any) => item.id);
  return idsOnlyArray;
};

export { getTableRows, getTableColumns, reorderArray };
